import React, { Fragment, useEffect, useState } from 'react';
import { get, last, split } from 'lodash/fp';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { DragDrop } from '@uppy/react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  ButtonToolbar,
  Icon,
  Input,
  Progress,
  Panel,
  Tag
} from 'rsuite';
import { getSaved, getSaving } from '../../../redux/Guest/selectors/file';
import * as PERMISSIONS from '../../../constants/permissions';
import { modifyAccess } from '../../../helpers/permission';
import { ClientId, ClientDomainId, AppConfig } from '../../../helpers/config';
import AccessControl from '../../AccessControl';
import uppyAwsS3Upload from '../../../helpers/uppyAwsS3Upload';
import {
  createGuestFile,
  fetchGuestFiles
} from '../../../redux/sagas/routines';
import { clearGuestFiles } from '../../../redux/Guest/actions/file';

const { Line: ProgressLine } = Progress;

const FileTag = styled(Tag)`
  margin-right: 20px;
`;

const FileUpload = ({ guestId, showForm, onHideForm, onShowForm }) => {
  const dispatch = useDispatch();
  const isSaved = useSelector(getSaved);
  const isSaving = useSelector(getSaving);
  const [label, setLabel] = useState('');
  const [fileName, setFileName] = useState(null);
  const [fileType, setFileType] = useState(null);
  const [fileSize, setFileSize] = useState(null);
  const [uppyUpload, setUppyUpload] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(null);
  const allowSave = fileName && label && !uploadProgress;
  const savingInProgress = !!uploadProgress || isSaving;
  const uploadStatus = uploadProgress < 100 ? 'active' : 'success';

  const resetFileUpload = () => {
    uppyUpload.reset();
    setFileName(null);
    setFileType(null);
    setFileSize(null);
    setUploadProgress(null);
  };

  useEffect(() => {
    setUppyUpload(
      uppyAwsS3Upload({
        baseDir: `client-${ClientId}/domain-${ClientDomainId}/guest/${guestId}`,
        bucket: get('buckets.files', AppConfig),
        onFileAdded: ({ data: { name } }) => {
          setFileName(name.replace(/ /g, '_'));
        },
        onUpload: () => {
          setUploadProgress(1);
        },
        onUploadProgress: (_, { bytesUploaded, bytesTotal }) => {
          const progress = (100 * bytesUploaded) / bytesTotal;
          setUploadProgress(progress);
          setFileSize(bytesTotal);
        },
        onUploadError: () => {
          resetFileUpload();
        },
        onComplete: result => {
          const fileUrl = get('successful.0.uploadURL', result);
          const name = last(split('/', fileUrl));
          const type = last(split('.', name));
          setFileName(name);
          setFileType(type);
        }
      })
    );
  }, []);

  useEffect(
    () => {
      if (fileName && fileType && label) {
        const body = {
          objId: guestId,
          fileName,
          fileType,
          fileSize,
          label
        };
        dispatch(createGuestFile(body));
      }
    },
    [fileName, fileType, label]
  );

  useEffect(
    () => {
      if (isSaved) {
        setLabel('');
        resetFileUpload();
        onHideForm();
        dispatch(clearGuestFiles());
        dispatch(fetchGuestFiles({ guestId }));
      }
    },
    [isSaved]
  );

  const handleChangeLabel = value => {
    setLabel(value);
  };

  const handleOnCreate = () => {
    onShowForm();
  };

  const handleSave = () => {
    if (fileName && uploadProgress === null) {
      uppyUpload.upload();
    }
  };

  return (
    <AccessControl allowOnlyFor={modifyAccess(PERMISSIONS.MANAGE_FILE)}>
      <ButtonToolbar style={{ textAlign: 'right' }}>
        {showForm ? (
          <Fragment>
            <Button
              onClick={handleSave}
              appearance="primary"
              disabled={!allowSave || isSaving}
            >
              <Icon icon="save" /> {isSaving ? 'Saving...' : 'Save'}
            </Button>
            <Button onClick={onHideForm}>
              <Icon icon="close-circle" /> Cancel
            </Button>
          </Fragment>
        ) : (
          <Button onClick={handleOnCreate} appearance="primary">
            <Icon icon="plus-circle" /> Create
          </Button>
        )}
      </ButtonToolbar>

      {showForm &&
        !fileName && (
          <Panel bordered style={{ marginTop: 10 }}>
            <DragDrop
              width="100%"
              height="130px"
              note={get('opts.note', uppyUpload)}
              uppy={uppyUpload}
            />
          </Panel>
        )}
      {fileName && (
        <Panel bordered style={{ marginTop: 10 }}>
          <FileTag
            onClick={resetFileUpload}
            closable={!savingInProgress}
            style={{ marginBottom: 10, width: 378 }}
          >
            {fileName}
          </FileTag>
          {uploadProgress && (
            <ProgressLine
              percent={Math.round(uploadProgress)}
              status={uploadStatus}
              style={{ width: 410, paddingTop: 0, paddingLeft: 0 }}
            />
          )}
          <Input
            name="label"
            value={label}
            onChange={handleChangeLabel}
            placeholder="Provide file label (required)"
            style={{ width: 378 }}
            disabled={savingInProgress}
          />
        </Panel>
      )}
    </AccessControl>
  );
};

FileUpload.propTypes = {
  guestId: PropTypes.number.isRequired,
  showForm: PropTypes.bool,
  onHideForm: PropTypes.func.isRequired,
  onShowForm: PropTypes.func.isRequired
};

FileUpload.defaultProps = {
  showForm: false
};

export default FileUpload;
